import { LogoImage } from 'customizations'
import { User } from 'api'
import { withErrorBoundary, Spinner } from 'shared'
import { SmartButtonStub, SmartButtonLink } from 'ui/smart-buttons'

class LoginPage extends Component {
  state = {
    email: '',
    password: '',
    settings: {},
    signInBannerFrame: null,
    signInBannerNatural: null,
  }

  constructor(props) {
    super(props)
    this.signInBannerFrameRef = React.createRef()
  }

  async componentDidMount() {
    window.addEventListener('resize', this.updateSignInBannerFrame)
    try {
      const settings = await Backend.get('/signup_settings')
      this.setState({ settings }, () => setTimeout(this.updateSignInBannerFrame, 0))
    } catch (err) {
      console.error('Failed to load signup settings:', err)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateSignInBannerFrame)
  }

  updateSignInBannerFrame = () => {
    const frame = this.signInBannerFrameRef?.current
    if (!frame) return

    const rect = frame.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return

    const nextFrame = { width: rect.width, height: rect.height }
    const currentFrame = this.state.signInBannerFrame
    if (
      currentFrame &&
      Math.abs(currentFrame.width - nextFrame.width) < 1 &&
      Math.abs(currentFrame.height - nextFrame.height) < 1
    ) {
      return
    }

    this.setState({ signInBannerFrame: nextFrame })
  }

  handleSignInBannerLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.target
    const nextNatural = { width: naturalWidth, height: naturalHeight }
    const currentNatural = this.state.signInBannerNatural

    if (
      !currentNatural ||
      currentNatural.width !== nextNatural.width ||
      currentNatural.height !== nextNatural.height
    ) {
      this.setState({ signInBannerNatural: nextNatural }, this.updateSignInBannerFrame)
      return
    }

    this.updateSignInBannerFrame()
  }

  signInPageBannerStyle(crop) {
    if (!crop) return null

    const x = parseFloat(crop.x)
    const y = parseFloat(crop.y)
    const w = parseFloat(crop.w)
    const h = parseFloat(crop.h)
    if (![x, y, w, h].every(Number.isFinite)) return null
    if (w <= 0 || h <= 0 || w > 1 || h > 1) return null

    const focusX = (x + w / 2) * 100
    const focusY = (y + h / 2) * 100
    const fallbackStyle = { objectPosition: `${focusX}% ${focusY}%` }

    const natural = this.state.signInBannerNatural
    const frame = this.state.signInBannerFrame
    if (!natural || !frame || natural.width <= 0 || natural.height <= 0 || frame.width <= 0 || frame.height <= 0) {
      return fallbackStyle
    }

    const cropWidth = natural.width * w
    const cropHeight = natural.height * h
    const scale = Math.max(frame.width / cropWidth, frame.height / cropHeight)
    const scaledCropWidth = cropWidth * scale
    const scaledCropHeight = cropHeight * scale
    const left = ((frame.width - scaledCropWidth) / 2) - (x * natural.width * scale)
    const top = ((frame.height - scaledCropHeight) / 2) - (y * natural.height * scale)

    return {
      position: 'absolute',
      top: 0,
      left: 0,
      maxWidth: 'none',
      width: `${natural.width * scale}px`,
      height: `${natural.height * scale}px`,
      transform: `translate(${left}px, ${top}px)`,
      transformOrigin: 'top left',
    }
  }

  renderSignInPageBanner() {
    const settings = this.state.settings || {}
    const url = settings.sign_in_page_banner
    if (!url) return null

    const style = this.signInPageBannerStyle(settings.sign_in_page_banner_crop)

    return (
      <img
        className="login-modal-banner-media"
        src={url}
        alt=""
        onLoad={this.handleSignInBannerLoad}
        style={style || undefined}
      />
    )
  }

  render() {
    return (
      <div className="LoginModal">
        <div className="left d-none d-sm-block position-relative overflow-hidden" ref={this.signInBannerFrameRef}>
          {this.renderSignInPageBanner()}
          <div className="left-overlay"></div>
          <p className="login-copyright">{application.assets.get('copyright')}</p>
        </div>
        <div className="right">
          {this.renderRightPane()}
        </div>
      </div>
    )
  }

  renderRightPane() {
    let loginEnabled = App.loginOption.includes('user')
    let signupEnabled = App.data.features.sign_up_enabled

    return (
      <>
        <img src={LogoImage} className='logo-image mb-2' />
        <section className="form">
          <h3 className="mb-3 d-none">Sign In</h3>

          { loginEnabled &&
            <form>
              <div className="labeled-inline-field">
                <label>Login</label>
                <input type="email" className="form-control"
                  placeholder="Email"
                  autoComplete="email"
                  name="email" value={this.state.email}
                  onChange={this.inputChanged}
                  onKeyPress={e => e.key == 'Enter' && this.logIn()}
                />
              </div>
              <div className="labeled-inline-field">
                <label>Password</label>
                <input type="password" className="form-control"
                  placeholder="Password"
                  autoComplete="current-password"
                  name="password" value={this.state.password}
                  onChange={this.inputChanged} />
              </div>
            </form>
          }

          { this.state.error &&
            <p className="alert alert-danger text-center">{this.state.error}</p>
          }

          { loginEnabled &&
            <p className="links text-right mb-2">
              <a className="forgot-password-link" href="/u/password/new">Forgot Password?</a>
            </p>
          }

          <div className="text-center">
            {
              loginEnabled &&
              <SmartButtonStub text="Sign In" buttonType="button" className="my-2 admin-purple px-5 inverted primary custom-font-size" click={this.logIn} />
            }
            {
              loginEnabled && signupEnabled &&
              <SmartButtonLink text="Create my Account" className="my-2 admin-purple px-5 inverted primary custom-font-size" to="/signup" />
            }
          </div>
        </section>
      </>
    )
  }

  logIn = async () => {
    let { email, password } = this.state
    this.doLogin('/auth', {email, password, device_type: 'browser'})
  }

  doLogin = async (url, params) => {
    try {
      let res = await Backend.post(url, params)
      console.info('auth as', res.user)
      User.signIn(res.user)
      this.setState({ error: null })
      window.location = '/app'
    } catch(xhr) {
      console.warn('auth failed', xhr)
      if (xhr.status >= 400 && xhr.status < 500) {
        this.setState({ error: xhr.responseJSON.message })
      }
    }
  }

  inputChanged = (e) => {
    let { name, value } = e.target
    this.setState({ [name]: value, error: null })
  }
}

export default withErrorBoundary(LoginPage)
